/**
 * ExamDialogs Component
 * All dialog components for exam mode
 */
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

// Confirm Submit Dialog
export const ConfirmSubmitDialog = ({ open, onOpenChange, onConfirm, unansweredCount, flaggedCount }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Submit Exam?</AlertDialogTitle>
        <AlertDialogDescription>
          {unansweredCount > 0 ? (
            <span className="text-amber-600">
              You have {unansweredCount} unanswered question{unansweredCount !== 1 ? 's' : ''}.
            </span>
          ) : (
            'You have answered all questions.'
          )}
          {flaggedCount > 0 && (
            <span className="block mt-2">
              {flaggedCount} question{flaggedCount !== 1 ? 's are' : ' is'} still flagged for review.
            </span>
          )}
          <span className="block mt-2">
            Are you sure you want to submit your exam?
          </span>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Continue Exam</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-[#0047AB] hover:bg-[#003380]">
          Submit Exam
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Time Warning Dialog
export const TimeWarningDialog = ({ open, onOpenChange }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
          <AlertTriangle className="w-5 h-5" />
          5 Minutes Remaining
        </AlertDialogTitle>
        <AlertDialogDescription>
          You have 5 minutes left to complete your exam. 
          Please review your answers and submit when ready.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={() => onOpenChange(false)}>
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Exit Confirm Dialog
export const ExitConfirmDialog = ({ open, onOpenChange, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          Exit Exam?
        </AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to exit the exam? Your progress will not be saved 
          and you will need to start over.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Continue Exam</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">
          Exit Exam
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Block End Dialog (PMP)
export const BlockEndDialog = ({ open, onOpenChange, onConfirm, blockNumber, totalBlocks, isLastBlock }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {isLastBlock ? 'Submit Final Block?' : `End Block ${blockNumber}?`}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {isLastBlock ? (
            'This is the final block. Once you end it, your exam will be submitted for grading.'
          ) : (
            <>
              Once you end Block {blockNumber}, you cannot return to change your answers. 
              You will have a 10-minute break before Block {blockNumber + 1}.
            </>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Continue Block</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-[#0047AB] hover:bg-[#003380]">
          {isLastBlock ? 'Submit Exam' : 'End Block & Take Break'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
